import React, { useEffect, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibChevronDownIcon, KibChevronUpIcon } from '@chewy/kib-icons-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import classNames from 'classnames'
import type { Nil } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import {
  getInvoiceGroupSubTotal,
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/components/dashboard/invoices/invoiceUtils'
import { MIN_QUANTITY } from '~/components/dashboard/soapV2/charges/constants'
import DialogNames from '~/constants/DialogNames'
import { OrderType } from '~/constants/SOAPStates'
import { toggleInvoiceGroupExpanded } from '~/store/actions/finance'
import { fetchUnifiedOrderByTypeAndLogId } from '~/store/actions/orders'
import { getIsFetchingClientCharges } from '~/store/duck/clientFinanceData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useOpenPrescriptionDialogAfterFetchUnifiedOrder } from '~/store/hooks/orders'
import { getCurrentUserId } from '~/store/reducers/auth'
import {
  getFinanceIsFetching,
  getFinanceIsSaving,
} from '~/store/reducers/finance'
import {
  getOrdersIsSending,
  getUnifiedOrder,
  getUnifiedOrderIsLoading,
} from '~/store/reducers/orders'
import { getPatientName } from '~/store/reducers/patients'
import { getClientId, getPatientId, getSoapId } from '~/store/reducers/soap'
import type { InvoiceLineItem } from '~/types'
import {
  convertInvoiceLineItemToSoapLog,
  convertRetailOrderLineItemToSoapLog,
} from '~/utils/orderUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import ApplyManualDiscountModal from './ApplyManualDiscountModal'
import ChirpSoapChargesTableFooter from './ChirpSoapChargesTableFooter'
import ChirpSoapChargesTableRow from './ChirpSoapChargesTableRow'
import { useChargeItemDecline } from './hooks/useChargeItemDecline'
import { useChargeItemDelete } from './hooks/useChargeItemDelete'
import { useChargeItemDiscount } from './hooks/useChargeItemDiscount'
import { useChargeItemDragDrop } from './hooks/useChargeItemDragDrop'
import { useOptimisticItemUpdates } from './hooks/useOptimisticItemUpdates'
import { separateItemsByDeclinedStatus } from './utils/tableHelpers'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import styles from './ChirpSoapChargesTable.module.scss'

export interface ChirpSoapChargesTableProps {
  chargesId: string | Nil
  editDisabled?: boolean
  isExpanded?: boolean
  items: (InvoiceLineItem | RetailOrderLineItem)[]
}

const ChirpSoapChargesTable = ({
  chargesId,
  editDisabled = false,
  isExpanded = false,
  items,
}: ChirpSoapChargesTableProps) => {
  const { t } = useTranslation(['Common', 'Payments', 'Validations'])
  const dispatch = useDispatch()
  const soapId = useSelector(getSoapId)
  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const patientName = useSelector(getPatientName(patientId))
  const modifierId = useSelector(getCurrentUserId)
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const isFetching = useSelector(getFinanceIsFetching)
  const isFetchingChargeSheet = useSelector(getIsFetchingClientCharges)
  const isSaving = useSelector(getFinanceIsSaving)
  const isSendingOrders = useSelector(getOrdersIsSending)

  // Vaccine details dialog hook
  const [openVaccineDetailsDialog] = useDialog(DialogNames.VACCINE_DETAILS)

  const isLoading =
    isFetching || isSaving || isSendingOrders || isFetchingChargeSheet

  // Optimistic updates hook - provides immediate UI feedback
  const { optimisticItems, applyOptimistic } = useOptimisticItemUpdates(items)

  const [localItems, setLocalItems] =
    useState<(InvoiceLineItem | RetailOrderLineItem)[]>(optimisticItems)

  const [discountModalOpen, setDiscountModalOpen] = useState(false)
  const [selectedItemForDiscount, setSelectedItemForDiscount] = useState<
    InvoiceLineItem | RetailOrderLineItem | null
  >(null)
  const [hasInvalidQuantity, setHasInvalidQuantity] = useState(false)

  // Check if any items have invalid quantity on mount and when items change
  useEffect(() => {
    const checkItemQuantity = (item: InvoiceLineItem | RetailOrderLineItem) => {
      const quantity = item.quantity ?? 0
      return Number.isNaN(quantity) || quantity < MIN_QUANTITY
    }

    const hasInvalid = localItems.some((item) => {
      // Check parent item
      if (checkItemQuantity(item)) {
        return true
      }

      // Check bundle children
      if (
        isGroupedInvoiceItem(item) &&
        !getIsRetailOrderLineItem(item) &&
        (item as InvoiceLineItem).items
      ) {
        return (item as InvoiceLineItem).items?.some(checkItemQuantity) ?? false
      }

      return false
    })

    setHasInvalidQuantity(hasInvalid)
  }, [localItems])

  // Selectors for unified order (used for vaccine dialog)
  const unifiedOrder = useSelector(getUnifiedOrder)

  // Prescription dialog hook
  const { fetchUnifiedOrderAndOpenPrescription } =
    useOpenPrescriptionDialogAfterFetchUnifiedOrder({
      isChargeSheet: true,
    })

  // Vaccine dialog - open after fetching unified order (matching prescription pattern)
  const openVaccineDialogAfterFetch = useCloseAfterCreation(
    (item: InvoiceLineItem | RetailOrderLineItem) => {
      const convertedItem = getIsRetailOrderLineItem(item)
        ? convertRetailOrderLineItemToSoapLog(item)
        : convertInvoiceLineItemToSoapLog(item)

      const orderToUse = unifiedOrder || convertedItem

      openVaccineDetailsDialog({
        clientId,
        order: {
          ...orderToUse,
          logId: item.logId ?? undefined,
          producerId: item.producerId ?? undefined,
          vaccinationDetails:
            unifiedOrder?.vaccinationDetails || orderToUse.vaccinationDetails,
        },
        patientId,
      })
    },
    getUnifiedOrderIsLoading,
  )

  // Shared hooks for item actions
  const { deleteItem, deleteBundle } = useChargeItemDelete(
    clientId,
    modifierId,
    soapId,
    true, // isFromSoap - charge sheet context
    applyOptimistic,
  )
  const { declineItem, declineBundle } = useChargeItemDecline(applyOptimistic)
  const applyDiscount = useChargeItemDiscount(applyOptimistic)
  const handleDragEnd = useChargeItemDragDrop(
    chargesId,
    localItems,
    setLocalItems,
    isChewyCheckoutEnabled,
  )

  // Sync localItems with optimisticItems (for drag-and-drop state)
  useEffect(() => {
    setLocalItems(optimisticItems)
  }, [optimisticItems])

  // Wrapper handlers that match the expected prop signatures
  const handleDeleteClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    deleteItem(item)
  }

  const handleDeclineClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
    parentItem?: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    declineItem(item, parentItem)
  }

  const handleBundleDeleteClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    deleteBundle(item as InvoiceLineItem)
  }

  const handleBundleDeclineClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    declineBundle(item as InvoiceLineItem)
  }

  // Get item ID for drag and drop - must be unique
  const getItemId = (
    item: InvoiceLineItem | RetailOrderLineItem,
    index: number,
  ) => {
    if (isGroupedInvoiceItem(item)) {
      const invoiceItem = item as InvoiceLineItem
      // Use group or fallback to id or index
      const groupKey = invoiceItem.group || invoiceItem.id || `idx-${index}`
      return `charges-table-group-${invoiceItem.soapId || 'no-soap'}_${groupKey}`
    }
    // Use id + logId, or just id, or fallback to index
    const itemKey = item.id || `idx-${index}`
    const logKey = item.logId || 'no-log'
    return `charges-table-${itemKey}_${logKey}`
  }

  // Toggle bundle expand/collapse
  const handleToggleExpand = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const groupId = getIsRetailOrderLineItem(item)
      ? null
      : (item as InvoiceLineItem).group
    dispatch(toggleInvoiceGroupExpanded(groupId || item.id, soapId))
  }

  // Handle calculator click to open discount modal
  const handleCalculatorClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    setSelectedItemForDiscount(item)
    setDiscountModalOpen(true)
  }

  // Handle applying discount
  const handleApplyDiscount = (
    discountAmount: number,
    quantity: number,
    discountPercentage?: number,
  ) => {
    if (!selectedItemForDiscount) {
      return
    }

    const item = selectedItemForDiscount
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (!isRetailOrder) {
      applyDiscount(
        item as InvoiceLineItem,
        discountAmount,
        quantity,
        discountPercentage,
      )
    }
    // Note: Retail order line items don't support discountAmount in their update input
    // Discounts for retail items are typically handled through different mechanisms

    setDiscountModalOpen(false)
    setSelectedItemForDiscount(null)
  }

  // Handle closing discount modal
  const handleCloseDiscountModal = () => {
    setDiscountModalOpen(false)
    setSelectedItemForDiscount(null)
  }

  // Separate active and declined items
  // For bundles with mixed children: split into two - one for active section, one for declined
  const { activeItems, declinedItems } = React.useMemo(
    () => separateItemsByDeclinedStatus(localItems),
    [localItems],
  )

  // State for declined section collapse
  const [declinedSectionExpanded, setDeclinedSectionExpanded] =
    useState<boolean>(false)

  // Toggle declined section
  const handleToggleDeclinedSection = () => {
    setDeclinedSectionExpanded(!declinedSectionExpanded)
  }

  // Handle prescription click to open Rx dialog
  const handlePrescriptionClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    // Only open prescription dialog for prescribable items
    const convertedItem = getIsRetailOrderLineItem(item)
      ? convertRetailOrderLineItemToSoapLog(item)
      : convertInvoiceLineItemToSoapLog(item)

    const { logId } = item
    const { type } = convertedItem
    if (logId) {
      fetchUnifiedOrderAndOpenPrescription(
        logId,
        getIsRetailOrderLineItem(item)
          ? OrderType.PRESCRIPTION
          : (type as OrderType),
      )
    }
  }

  // Fetch unified order and open vaccine dialog (matching prescription pattern)
  const fetchUnifiedOrderAndOpenVaccine = (
    logId: string,
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    dispatch(fetchUnifiedOrderByTypeAndLogId(logId, OrderType.PROCEDURE))
    openVaccineDialogAfterFetch(item)
  }

  // Handle vaccine click to open vaccine details dialog (matching prescription pattern)
  const handleVaccineClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const { logId } = item
    if (logId) {
      fetchUnifiedOrderAndOpenVaccine(logId, item)
    } else {
      // For new items without logId, open immediately with converted data
      const convertedItem = getIsRetailOrderLineItem(item)
        ? convertRetailOrderLineItemToSoapLog(item)
        : convertInvoiceLineItemToSoapLog(item)

      openVaccineDetailsDialog({
        clientId,
        order: {
          ...convertedItem,
          producerId: item.producerId ?? undefined,
        },
        patientId,
      })
    }
  }

  // Calculate total
  const total = getInvoiceGroupSubTotal(items)
  const displayPatientName: string = patientName || t('Common:PET')

  // Column definitions for skeleton rendering
  const columns = [
    { key: 'item', className: styles.dataCellItem },
    { key: 'producer', className: styles.dataCellProducer },
    { key: 'quantity', className: styles.dataCellQuantity },
    { key: 'actions', className: styles.dataCellActions },
    { key: 'total', className: styles.dataCellTotal },
    { key: 'originalPrice', className: styles.dataCellOriginalPrice },
  ]

  // Number of skeleton rows to show while loading (including bundle children)
  const skeletonRowCount =
    localItems.reduce((count, item) => {
      const isBundle = isGroupedInvoiceItem(item)
      const isRetailOrder = getIsRetailOrderLineItem(item)
      const bundleChildCount =
        isBundle && !isRetailOrder
          ? ((item as InvoiceLineItem).items?.length ?? 0)
          : 0
      return count + 1 + bundleChildCount
    }, 0) || 4

  // Hide table when no items and not loading
  // R.isEmpty(undefined) returns false, so we need explicit check
  const hasItems = items && items.length > 0
  if (!hasItems && !isLoading) {
    return null
  }

  return (
    <div
      className={classNames(styles.tableContainer, {
        [styles.tableContainerExpanded]: isExpanded,
      })}
    >
      {/* Validation Alert */}
      {hasInvalidQuantity && (
        <KibTopLevelAlert
          heading={t('Validations:QUANTITY_MUST_BE_AT_LEAST_MIN', {
            min: MIN_QUANTITY,
          })}
          severity="error"
        />
      )}

      <div
        className={classNames(styles.tableWrapper, {
          [styles.tableWrapperExpanded]: isExpanded,
        })}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr className={styles.headerRow}>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellItem,
                  )}
                >
                  {t('Common:ITEM')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellProducer,
                  )}
                >
                  {t('Common:PRODUCER_FINANCIAL')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellQuantity,
                  )}
                >
                  {t('Common:QUANTITY')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellActions,
                  )}
                >
                  {t('Common:ACTIONS')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellTotal,
                  )}
                >
                  {t('Common:TOTAL')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellOriginalPrice,
                  )}
                >
                  {/* Empty header for original price column */}
                </th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody data-testid="charges-table-skeleton">
                {Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                  <tr
                    className={styles.skeletonRow}
                    key={`skeleton-${rowIndex}`}
                  >
                    {columns.map((col) => (
                      <td
                        className={classNames(styles.dataCell, col.className)}
                        key={col.key}
                      >
                        <KibSkeletonText lines={1} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <>
                {/* Active Items Section */}
                <Droppable droppableId="charges-table-active" type="CHARGES">
                  {(droppableProvided) => (
                    <tbody
                      ref={(ref) => {
                        droppableProvided.innerRef(ref)
                      }}
                      {...droppableProvided.droppableProps}
                    >
                      {activeItems.map((item, index) => {
                        const itemId = `active-${getItemId(item, index)}`
                        const isBundle = isGroupedInvoiceItem(item)
                        const isRetailOrder = getIsRetailOrderLineItem(item)
                        const bundleItems =
                          isBundle && !isRetailOrder
                            ? ((item as InvoiceLineItem).items ?? [])
                            : []

                        return (
                          <Draggable
                            draggableId={itemId}
                            index={index}
                            key={itemId}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <ChirpSoapChargesTableRow
                                applyOptimistic={applyOptimistic}
                                bundleItems={bundleItems}
                                dragHandleProps={
                                  draggableProvided.dragHandleProps
                                }
                                draggableProps={
                                  draggableProvided.draggableProps
                                }
                                editDisabled={editDisabled}
                                isBundle={isBundle}
                                isDragging={draggableSnapshot.isDragging}
                                item={item}
                                ref={draggableProvided.innerRef}
                                soapId={soapId}
                                onBundleDeclineClick={handleBundleDeclineClick}
                                onBundleDeleteClick={handleBundleDeleteClick}
                                onCalculatorClick={handleCalculatorClick}
                                onDeclineClick={handleDeclineClick}
                                onDeleteClick={handleDeleteClick}
                                onPrescriptionClick={handlePrescriptionClick}
                                onToggleExpand={handleToggleExpand}
                                onVaccineClick={handleVaccineClick}
                              />
                            )}
                          </Draggable>
                        )
                      })}
                      {droppableProvided.placeholder}
                    </tbody>
                  )}
                </Droppable>

                {/* Declined Items Section */}
                {declinedItems.length > 0 && (
                  <>
                    <tbody>
                      <tr className={styles.declinedHeaderRow}>
                        <td className={styles.declinedHeaderCell} colSpan={6}>
                          <KibButtonNew
                            aria-expanded={declinedSectionExpanded}
                            aria-label={
                              declinedSectionExpanded
                                ? t('Common:COLLAPSE_DECLINED_ITEMS')
                                : t('Common:EXPAND_DECLINED_ITEMS')
                            }
                            className={styles.declinedHeaderButton}
                            emphasis="tertiary"
                            icon={
                              declinedSectionExpanded ? (
                                <KibChevronUpIcon />
                              ) : (
                                <KibChevronDownIcon />
                              )
                            }
                            onClick={handleToggleDeclinedSection}
                          >
                            <span className={styles.declinedHeaderText}>
                              {t('Payments:DECLINED_BY_CLIENT_COUNT', {
                                value: declinedItems.length,
                              })}
                            </span>
                          </KibButtonNew>
                        </td>
                      </tr>
                    </tbody>

                    {declinedSectionExpanded && (
                      <tbody>
                        {declinedItems.map((item, index) => {
                          const itemId = `declined-${getItemId(
                            item,
                            activeItems.length + index,
                          )}`
                          const isBundle = isGroupedInvoiceItem(item)
                          const isRetailOrder = getIsRetailOrderLineItem(item)
                          const bundleItems =
                            isBundle && !isRetailOrder
                              ? ((item as InvoiceLineItem).items ?? [])
                              : []

                          return (
                            <ChirpSoapChargesTableRow
                              applyOptimistic={applyOptimistic}
                              bundleItems={bundleItems}
                              editDisabled={editDisabled}
                              isBundle={isBundle}
                              item={item}
                              key={itemId}
                              soapId={soapId}
                              onBundleDeclineClick={handleBundleDeclineClick}
                              onBundleDeleteClick={handleBundleDeleteClick}
                              onCalculatorClick={handleCalculatorClick}
                              onDeclineClick={handleDeclineClick}
                              onDeleteClick={handleDeleteClick}
                              onPrescriptionClick={handlePrescriptionClick}
                              onToggleExpand={handleToggleExpand}
                              onVaccineClick={handleVaccineClick}
                            />
                          )
                        })}
                      </tbody>
                    )}
                  </>
                )}
              </>
            )}
          </table>
        </DragDropContext>

        {/* Footer with total - inside wrapper so Producer dropdown can stack above it */}
        <ChirpSoapChargesTableFooter
          patientName={displayPatientName}
          total={total}
        />
      </div>

      {/* Discount Modal */}
      <ApplyManualDiscountModal
        item={selectedItemForDiscount}
        open={discountModalOpen}
        onApplyDiscount={handleApplyDiscount}
        onClose={handleCloseDiscountModal}
      />
    </div>
  )
}

export default ChirpSoapChargesTable
